import { useCallback, useState, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';

interface CopyableFieldProps {
  /** Texto copiado para a área de transferência. */
  value: string;
  /** Rótulo para aria-label (ex.: "CPF"). */
  label: string;
  children: ReactNode;
  className?: string;
}

/** Campo + botão de copiar ao lado (funciona mesmo com input disabled/readOnly). */
export function CopyableField({ value, label, children, className }: CopyableFieldProps) {
  const [copied, setCopied] = useState(false);
  const text = value.trim();

  const handleCopy = useCallback(async () => {
    if (!text) {
      toast.error('Nada para copiar.');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copiado!', { duration: 1600 });
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error('Não foi possível copiar.');
    }
  }, [text]);

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <div className="min-w-0 flex-1">{children}</div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-10 shrink-0 text-muted-foreground hover:text-foreground"
        aria-label={`Copiar ${label}`}
        disabled={!text}
        onClick={() => void handleCopy()}
      >
        {copied ? <Check className="size-4 text-success" /> : <Copy className="size-4" />}
      </Button>
    </div>
  );
}
